import { Briefcase, GraduationCap, Server, Target, TrendingUp, Users } from 'lucide-react';
import { Link } from 'react-router-dom';

const OurPeople = () => {
  const expertise = [
    {
      icon: <Briefcase className="w-8 h-8 text-[#007BFF]" />,
      title: 'Energy Analysts',
      description: 'Specialists in commodity markets, supply chains, and pricing dynamics'
    },
    {
      icon: <GraduationCap className="w-8 h-8 text-[#007BFF]" />,
      title: 'Economists',
      description: 'Experts in macroeconomic drivers, policy analysis, and market forecasting'
    },
    {
      icon: <Users className="w-8 h-8 text-[#007BFF]" />,
      title: 'Data Scientists',
      description: 'Advanced analytics, modeling, and quantitative research capabilities'
    },
    {
      icon: <Target className="w-8 h-8 text-[#007BFF]" />,
      title: 'Strategic Advisors',
      description: 'Former industry executives and policy professionals'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-[#1F7A4F] via-[#2C6E73] to-[#284A86] text-white py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 whitespace-nowrap">Our People</h1>
            <p className="text-base lg:text-lg text-white/90 leading-relaxed">
              Expert-driven intelligence powered by multidisciplinary expertise across energy commodities and financial markets.
            </p>
          </div>
        </div>
      </section>

      {/* Team Overview */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-[#1F3C88] mb-3">Who We Are</h2>
            <p className="text-lg text-slate-600 leading-relaxed max-w-3xl mx-auto">
              Enerlligence is composed of energy analysts, economists, data scientists, and strategic advisors with deep expertise across energy commodities, policy systems, and financial markets.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {expertise.map((item, index) => (
              <div key={index} className="text-center p-6 bg-slate-50 rounded-lg border border-slate-200 hover:border-[#007BFF] transition-all">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-white rounded-full mb-4 border-2 border-[#007BFF]">
                  {item.icon}
                </div>
                <h3 className="text-lg font-semibold text-[#1F3C88] mb-3">{item.title}</h3>
                <p className="text-slate-600">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Three Sections - Horizontal Grid */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Strategic Leadership */}
            <div className="bg-white p-8 rounded-lg border border-slate-200 flex flex-col h-full">
              <div className="flex items-start space-x-3 mb-6">
                <Target className="w-8 h-8 text-[#007BFF] flex-shrink-0 mt-1" />
                <h3 className="text-2xl font-bold text-[#1F3C88]">Strategic Leadership</h3>
              </div>
              <div className="space-y-4 text-base text-slate-600 leading-relaxed flex-grow">
                <p>
                  Enerlligence's strategic leadership team brings decades of combined experience across energy markets, financial analysis, and institutional advisory roles. This executive oversight ensures our intelligence platform maintains analytical rigor, independence, and relevance to client strategic priorities.
                </p>
                <p>
                  Our leadership provides governance over research methodologies, quality assurance frameworks, and platform development priorities. They ensure analytical outputs meet institutional standards for credibility, transparency, and decision-utility.
                </p>
                <p>
                  With backgrounds spanning energy companies, investment banking, consulting, and policy institutions, our leadership understands the intelligence requirements of diverse client segments.
                </p>
              </div>
            </div>

            {/* Research & Analytics */}
            <div className="bg-white p-8 rounded-lg border border-slate-200 flex flex-col h-full">
              <div className="flex items-start space-x-3 mb-6">
                <TrendingUp className="w-8 h-8 text-[#007BFF] flex-shrink-0 mt-1" />
                <h3 className="text-2xl font-bold text-[#1F3C88]">Research & Analytics</h3>
              </div>
              <div className="space-y-4 text-base text-slate-600 leading-relaxed flex-grow">
                <p>
                  Our research and analytics team comprises commodity specialists, country analysts, and thematic researchers covering the full spectrum of global energy markets. This multidisciplinary structure enables comprehensive intelligence.
                </p>
                <p>
                  Commodity research analysts develop supply-demand models, price forecasts, and trade flow analysis across oil, gas, coal, power, and emerging energy sources. They maintain structured databases and track infrastructure developments.
                </p>
                <p>
                  The research team follows rigorous analytical frameworks that ensure consistency, transparency, and quality control. All analysis undergoes peer review and quality assurance processes before publication.
                </p>
              </div>
            </div>

            {/* Data And Technology */}
            <div className="bg-white p-8 rounded-lg border border-slate-200 flex flex-col h-full">
              <div className="flex items-start space-x-3 mb-6">
                <Server className="w-8 h-8 text-[#007BFF] flex-shrink-0 mt-1" />
                <h3 className="text-2xl font-bold text-[#1F3C88]">Data And Technology</h3>
              </div>
              <div className="space-y-4 text-base text-slate-600 leading-relaxed flex-grow">
                <p>
                  Enerlligence's data and technology capabilities provide the infrastructure for structured intelligence delivery. Our platform aggregates energy market data from diverse sources and applies validation protocols.
                </p>
                <p>
                  The data architecture supports comprehensive coverage of commodity prices, production volumes, trade flows, infrastructure capacity, and policy developments across 200+ countries. Automated systems ensure data integrity.
                </p>
                <p>
                  Our analytics infrastructure includes quantitative modeling capabilities, scenario analysis tools, and visualization platforms that translate complex datasets into actionable intelligence.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Commitment Statement */}
      <section className="py-20 bg-white border-t border-slate-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Users className="w-16 h-16 text-[#007BFF] mx-auto mb-6" />
          <h2 className="text-3xl font-bold text-[#1F3C88] mb-6">Commitment to Excellence</h2>
          <p className="text-xl text-slate-700 leading-relaxed mb-6">
            Our team is united by a commitment to research integrity, structured methodologies, and delivering intelligence that materially impacts strategic outcomes.
          </p>
          <p className="text-lg text-slate-600 leading-relaxed">
            We combine technical expertise with institutional perspective to provide analysis that meets the highest standards of credibility and relevance.
          </p>
        </div>
      </section>
    </div>
  );
};

export default OurPeople;
